import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ContextService, LoginData, ServerServiceConfig } from '../../context.service';
import { GroupWrapperComponent } from '../capture-form/group-wrapper.component';
import { FormControl, Validators } from '@angular/forms';
import { StepDefinition } from '../dynamic';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { FormlyFieldConfig } from '@ngx-formly/core';

const thisYear = (new Date()).getFullYear();

/* Allow us to regain stack frame from microform calls */
let hostComp: PaymentFormComponent;
function onLoadCyberSource(): void {
  hostComp.bindCyberSourceEmbeddedForm();
}
@Component({
  selector: 'payment-form',
  templateUrl: './payment-form.component.html',
  styleUrls: ['./payment-form.component.scss'],
  standalone: false
})
export class PaymentFormComponent extends GroupWrapperComponent implements OnInit, OnDestroy {
  @ViewChild('cardholderName') cardholderName: HTMLInputElement | undefined;
  @ViewChild('spinner') spinner: HTMLInputElement | undefined;
  appConfig: ServerServiceConfig | undefined;
  nameFormControl = new FormControl('', [
    Validators.pattern(/^\s*\w+\s\w+\s*$/)
  ]);

  public paying: boolean = false;
  paymentConfig: any;
  // for external payments (Transact)

  cost: number | undefined;
  studentEmail: string | undefined;
  studentFirstName: string | undefined;
  studentLastName: string | undefined;

  // for embedded payments (CyberSource)
  microform: any;
  flexResponse: any;
  monthValue: string | undefined;
  yearValue: string | undefined;
  serviceContext: any;
  monthList: number[] = Array.from({ length: 12 }, (_, i) => i + 1);
  yearList: number[] = Array.from({ length: 6 }, (_, i) => i + thisYear);
  payByCheck: boolean = false;
  fieldSubscribed: boolean = false;
  isPaid: boolean = false;
  inlineLoaded: boolean = false;
  subscriptions: Subscription [] = [];
  country: string = 'USA';
  static application : string = 'key.mad_person_profile-id';

  constructor(public http: HttpClient, public context: ContextService) {
    super(http);
    hostComp = this;
  }

  get appearance(): MatFormFieldAppearance {
    return !!this.appConfig?.fill ? 'fill' : 'outline';
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.props?.capForm?.debug) {
      console.log('Payment %O (%O) %O', this.field.key, this.field.formControl?.value, this.field.model);
    }

    this.subscriptions.push(this.context.subscribeTo("initiatePayment", (initiate: boolean) => {
      if (initiate) {
        this.createEmbeddedContext();
      }
    }));
    this.subscriptions.push(this.context.subscribeTo("readyToReceive", (isReady: boolean) => {
      if (isReady) {
        this.checkComplete();
        this.formControl.valueChanges.subscribe({ next: (val) => this.checkComplete() });

        if (!this.fieldSubscribed) {
          this.fieldSubscribed = true;
          let checkBox = this.formState.ui.getFieldConfig(this.props.stepName, 'pay_method.mad_person-extra.check');
          if (!!checkBox) {
            this.payByCheck = (checkBox.formControl.value == '0');
            checkBox.formControl.valueChanges.subscribe({
              next: (val: any) => {
                if (this.debug) { console.log('Payment Pay-by-Check %O', val); }
                this.payByCheck = (val == '0');
                if (!!(this.props.transactionReference = this.formState.ui.getValue(this.props.stepName, 'mad_person_profile-extra.receipt_id'))) {
                  this.formControl.setErrors(null);
                }
                this.createEmbeddedContext();
              }
            });
          }
        }
      }
    }));
    // this.context.subscribeTo("documentId", (documentId) => {
    //   this.applicationNumber = documentId;
    // });
    this.subscriptions.push(this.context.subscribeTo("loginData", (loginData: LoginData) => {
      this.studentEmail = loginData.email;
      this.studentFirstName = loginData.firstname;
      this.studentLastName = loginData.lastname;
    }));
    this.subscriptions.push(this.context.subscribeTo("applicationInfo", (config: ServerServiceConfig) => {
      if (this.appConfig === undefined || !(this.appConfig.serviceVersion == config.serviceVersion && this.appConfig.serviceUrl == config.serviceUrl)) {
        this.appConfig = config;
        this.paymentConfig = this.appConfig?.integrations?.find(integration => !!integration.payment);
        this.cost = this.paymentConfig?.cost;
        if (this.externalPaymentConfig) {
          try {
            this.context.loadScript("https://train.cashnet.com/rapidcheckout/js/checkout.js");
          }
          catch {
            console.log('cannot setup Transact - CashNet');
          }
        }
      }
    }));
    if (this.props?.isPaid) {
      const cntl = this.props.ui.getControl(this.props.isPaid);
      if (cntl) {
        cntl.valueChanges.subscribe((newValue: any) => {
          this.isPaid = (newValue == '1');
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach( sub => sub.unsubscribe() );
    this.subscriptions = [];
  }
  get isHidden() {
    return this.field?.hide ?? !!this.props.transactionReference;
  }
  get externalPaymentConfig() {
    return this.paymentConfig?.type == 'external';
  }
  get inlinePaymentConfig() {
    return this.paymentConfig?.type == 'inline';
  }
  get formattedCost() {
    return `$ ${this.externalPaymentConfig ? ((this.cost ?? 0) / 100) : this.cost}`;
  }
  get buttonColor() {
    return !this.props.displayMessage?.startsWith('AUTHORIZED') ? 'warn' : 'primary'
  }
  /**
   * Determine if the group and the required subfields are filled in as well as if the form is ready
   */
  get isComplete(): boolean {
    let fields: FormlyFieldConfig[] = this.childFields?.filter(fld => fld.type != 'formly-template' && !!fld.props?.required) ?? [], current, isSet: boolean = true;
    while (!!(current = fields.shift())) {
      if (typeof current === 'object') {
        let kids = current?.fieldGroup?.filter(fldDefn => fldDefn.type != 'formly-template' && !!fldDefn.props?.required);
        if (!!kids && kids.length > 0) {
          fields = fields?.concat(kids);
        } else {
          isSet &&= !!current.form?.value[<string>current.key];
        }
      }
    }
    if (!!this.props.enableAfterValid) {
      isSet &&= this.props.dataModel.every((step: StepDefinition) => {
        let invalidCount: number = step.invalidValues.length;
        if (step.step == this.props.stepName && this.formControl.errors?.payment) {
          invalidCount--;
        }
        return invalidCount == 0;
      });
    }
    // This enables the button if the entire form is complete 
    return (this.externalPaymentConfig || (!!this.nameFormControl.valid && this.nameFormControl.value != '' && !!this.monthValue && !!this.yearValue)) && !!isSet;
  }

  /**
   * Create a merchant code that gets us back to the 
   */
  get applicationNumber(): string {
    let data = this.props.dataModel.find((model: any) => model.step == 'education');
    let appNum = data?.dataModel?.[PaymentFormComponent.application];
    const now = new Date();

    return `${now.getFullYear()}${now.getMonth().toString().padStart(2, '0')}${now.getDay().toString().padStart(2, '0')}${appNum?.toString(16).padStart(8, '0')}`;
  }

  checkComplete() {
    this.formControl.setErrors(!this.props.transactionReference ? { payment: "Please pay the fee using the Pay Fee button" } : null);

  }
  updateCost(event: any) {
    this.cost = event.target.value;
  }


  /**
   * Invoke the payment service (Transact) based on the data we have
   */
  invokeExternalSite() {
    // This is for a redirect payment (popup information after initializing the payment processor)
    const profileId = this.props.capForm.formModel[PaymentFormComponent.application];
    this.http.post('$service/initiate-payment', {
      paymentAmount: this.cost,
      transaction: this.applicationNumber?.padStart(8, '0') ?? 'fffffffe',
      studentFirstName: this.studentFirstName,
      studentLastName: this.studentLastName,
      studentEmail: this.studentEmail,
      currentLayout: this.context.layout?.layoutId,
      currentStep: this.props.stepName,
      profile: profileId
    }).subscribe({
      error: (error: any) => {
        console.error('Payment site cannot be invoked. %O', error);
      },
      next: (result: any) => {
        if (!!result.session) {
          let RapidCheckout = (<any>window).RapidCheckout;
          RapidCheckout.startCheckoutSession(result.session, { displayType: RapidCheckout.TransactDisplayType.redirect });
        } else {
          console.error('Server call did not return sessionid for redirect %O', result);
        }
      }
    });
  }
  clearFields() {
    // this.cardholderName = '';
    this.monthValue = undefined;
    this.yearValue = undefined;
  }
  /**
   * Start the process of connecting the remote processing agent to local
   */
  createEmbeddedContext() {
    if (this.inlinePaymentConfig) {
      let parameters: any = {
        career: this.formState.ui.getValue('education', 'intention.mad_person_profile-career'),
        program: this.formState.ui.getValue('education', 'intention.mad_person_profile-program'),
        plan: this.formState.ui.getValue('education', 'intention.mad_person_profile-plan'),
        subplan: this.formState.ui.getValue('education', 'intention.mad_person_profile-subplan'),
        baseId: this.applicationNumber,
        profile: this.props.capForm.formModel[PaymentFormComponent.application],
        cardholder: this.nameFormControl.value,
        currentLayout: this.context.layout?.layoutId,
        currentStep: this.props.stepName
      };
      this.http.get('$service/generate-capture-context', { params: parameters }).subscribe({
        next: (captureContext: any) => {
          // the capture context requested server-side for this transaction returned as JWT data object
          if (!(this.serviceContext = captureContext.jwt)) {
            try {
              console.error(JSON.parse(captureContext.error));
            } catch (err) {
              console.error('payment context %O error %O', captureContext, err);
            }
          };
          this.cost = captureContext.cost;

          this.formControl.updateValueAndValidity();

          // Extracting JWK in Body of Capture Context - JSON.stringify(JSON.parse(decodedPayload)['flx']['jwk'])
          if (!!this.serviceContext && !this.inlineLoaded) {
            try {
              // Here is the code to load the CyberSource client library into this window context
              const cardContext = JSON.parse(atob(this.serviceContext.split('.')[1])).ctx[0].data;
              this.context.loadScript(cardContext.clientLibrary,cardContext.clientLibraryIntegrity, onLoadCyberSource );
              this.inlineLoaded = true;
            } catch (err) {
              console.error('Problem with loading the script %O', err);
            }
          }
        },
        error: (error) => {
          console.log('ERROR %O\n%O', error.error, error.message);
          this.context.changeMessage(`Error generating payment data: ${error.error}`, 'Acknowledge');
        }
      });
    }
  }

  /**
   * This is for embedded UI payments
   */
  bindCyberSourceEmbeddedForm() {
    let Flex = (<any>window).Flex;
    // Check if Flex is available in the global scope
    if (!Flex) {
      console.log('FLEX not found!');
      return;
    }
    let flex = new Flex(this.serviceContext); //should be header.payload.signature?

    // custom styles that will be applied to each field we create using Microform
    this.microform = flex.microform({
      styles: {
        input: {
          color: '#555',
          'font-size': '16px'
        },
        valid: { color: '#3c763d' },
        invalid: { color: '#a94442' },
        ':focus': { color: 'blue' },
        ':disabled': { cursor: 'not-allowed' },
      }
    });
    this.microform.createField('number', { placeholder: 'Card Number' }).load('#numberContainer');
    this.microform.createField('securityCode', { placeholder: 'CVV' }).load('#securityContainer');

  }
  /**
   * Convert the numeric value to displayable text for the select
   * @param monthNumber 
   * @returns 
   */
  getMonthName(monthNumber: number): string {
    const date = new Date();
    date.setMonth(monthNumber - 1);

    return `(${(monthNumber).toString().padStart(2, "0")}) ${date.toLocaleString('en-US', { month: 'long', })}`;
  }
  /**
   * Cause the payment to start
   */
  invokePayment() {
    this.paying = true
    if (this.externalPaymentConfig) {
      this.invokeExternalSite();
    } else if (this.inlinePaymentConfig) {
      this.invokeInLinePayment();
    }
  }
  invokeInLinePayment() {
    this.context.changeMessage('');
    // The transient token is issued as a JSON Web Token (RFC 7519)
    // Example: xxxxx.yyyyy.zzzzz - Header / Payload / Signature
    var options = {
      expirationMonth: this.monthValue?.toString().padStart(2, "0"),
      expirationYear: this.yearValue?.toString().padStart(4, "0")
    };
    this.microform.createToken(options, this.payFee);
  }

  async payFee(err: any, token: string) {
    if (err) {
      console.error( 'Inline error returns %O', err)
      //globalComponent.displayMessage = err.message ?? 'Please provide both the first and last name from the credit card';
      hostComp.context.changeMessage(err.message, 'Acknowledge');
      //hostComp.cardholderName.nativeElement.focus();
      hostComp.paying = false;
    } else {
      let dataToPass = {
        token: token,
        career: hostComp.formState.ui.getValue('education', 'intention.mad_person_profile-career'),
        program: hostComp.formState.ui.getValue('education', 'intention.mad_person_profile-program'),
        plan: hostComp.formState.ui.getValue('education', 'intention.mad_person_profile-plan'),
        subplan: hostComp.formState.ui.getValue('education', 'intention.mad_person_profile-subplan'),
        postalCode: hostComp.formState.ui.getValue(hostComp.props.stepName, 'billing_address.billing_postal') ?? null,
        city: hostComp.formState.ui.getValue(hostComp.props.stepName, 'billing_address.billing_city') ?? null,
        state: hostComp.formState.ui.getValue(hostComp.props.stepName, 'billing_address.billing_state') ?? null,
        address1: hostComp.formState.ui.getValue(hostComp.props.stepName, 'billing_address.billing_address1') ?? null,
        country: hostComp.formState.ui.getValue(hostComp.props.stepName, 'billing_address.billing_country') ?? null,
        baseId: hostComp.applicationNumber,
        profile: hostComp.props.capForm.formModel[PaymentFormComponent.application],
        cardholder: hostComp.nameFormControl?.value,
        currentLayout: hostComp.context.layout?.layoutId,
        currentStep: hostComp.props.stepName
      };
      try {
        hostComp.paying = true;
        // At this point you may pass the token back to your server
        hostComp.http.post('$service/pay-cybersource', dataToPass).subscribe({
          next: (result: any) => {
            console.log('success %O', result);
            hostComp.props.displayMessage = result.status;
            hostComp.formState.ui.setValue(hostComp.props.stepName, 'mad_person_profile-isPaid', (hostComp.props.transactionReference = result.ref) ? '1' : '0');
            hostComp.paying = false;
            hostComp.checkComplete();
          },
          error: (error) => {
            console.error('error %O', error);
            hostComp.checkComplete();
            hostComp.paying = false;
            hostComp.context.setProgressIndeterminate(false);
            // globalComponent.displayMessage = error.message;
            hostComp.context.changeMessage(error.message, 'Acknowledge');
            hostComp.clearFields();
            hostComp.createEmbeddedContext();
          }
        });
      } catch (err) {
        console.error(' Problem submitting data to process %O', err);
      }
    }
  }
}